// One payout status for a creator, shared by the API and the account page so
// the Payouts tab and the plugin form say the same thing.
//
// not_started      no Stripe account saved yet
// action_required  Stripe needs more information from the creator
// in_review        everything is submitted and Stripe is verifying it
// rejected         Stripe declined the account
// enabled          payouts and transfers are on; paid plugins can be sold

const REVIEW_REASONS = /pending_verification|under_review/;

export function payoutStatus({
  hasAccount = true,
  payoutsEnabled = false,
  transfersActive = false,
  due = [],
  detailsSubmitted = false,
  disabledReason = null,
} = {}) {
  if (!hasAccount) return "not_started";
  if (payoutsEnabled && transfersActive) return "enabled";
  const reason = String(disabledReason || "");
  if (reason.startsWith("rejected")) return "rejected";
  if (due.length) return "action_required";
  if (detailsSubmitted || REVIEW_REASONS.test(reason)) return "in_review";
  return "action_required";
}

// Live Stripe account object → status.
export function payoutStatusFromStripeAccount(account) {
  const requirements = account?.requirements || {};
  return payoutStatus({
    payoutsEnabled: Boolean(account?.payouts_enabled),
    transfersActive: account?.capabilities?.transfers === "active",
    due: [...(requirements.currently_due || []), ...(requirements.past_due || [])],
    detailsSubmitted: Boolean(account?.details_submitted),
    disabledReason: requirements.disabled_reason || null,
  });
}

// Stored creator_accounts row → status, for when Stripe cannot be reached.
export function payoutStatusFromRow(row) {
  if (!row) return "not_started";
  let due = [];
  try {
    const parsed = JSON.parse(row.requirements_due || "[]");
    if (Array.isArray(parsed)) due = parsed;
  } catch {
    due = [];
  }
  return payoutStatus({
    payoutsEnabled: Boolean(row.payouts_enabled),
    transfersActive: Boolean(row.transfers_active),
    due,
    disabledReason: row.disabled_reason || null,
  });
}

export const PAYOUT_STATUS_TEXT = {
  not_started: {
    summary: "Payouts are not set up yet.",
    paid: "Paid plugins need payouts. Set them up in the Payouts tab first.",
  },
  action_required: {
    summary:
      "Stripe needs more information before it can pay you. Continue payout setup to provide it.",
    paid: "Stripe needs more information before you can sell plugins. Continue payout setup in the Payouts tab.",
  },
  in_review: {
    summary:
      "Stripe is reviewing your payout details. This usually takes a few minutes, sometimes a day or two. You don't need to do anything. Reload this page later to check.",
    paid: "Stripe is still reviewing your payout details. You can register a paid plugin once the review is done. Free plugins are not affected.",
  },
  rejected: {
    summary:
      "Stripe declined payouts for this account, so paid plugins cannot be sold from it. Contact us if you think this is a mistake.",
    paid: "Stripe declined payouts for this account, so paid plugins cannot be sold from it.",
  },
  enabled: {
    summary: "Payouts are enabled. Buyers can purchase your plugins.",
    paid: "",
  },
};

export function payoutStatusText(status) {
  return PAYOUT_STATUS_TEXT[status] || PAYOUT_STATUS_TEXT.action_required;
}
